// external
import React, { useState } from 'react';
import * as _ from 'lodash';
import { useForm } from 'react-hook-form';
import { gql, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ExclamationCircleIcon, PlusSmIcon } from '@heroicons/react/solid';
// internal
import useNotifier from '../../hooks/useNotifier';
import {
  Account_AccountData_account,
  Account_AccountData_account_users,
} from '../../types/graph/Account_AccountData';

// Component interface(s)
interface IProps {
  account: Account_AccountData_account;
}

// Form data validation schema
const validationSchema = yup.object().shape({
  person: yup.object().shape({
    name: yup.object().shape({
      full: yup
        .string()
        .matches(/^[A-Za-z -]*$/, 'Please enter a valid name.')
        .max(50)
        .required('Full name is required.'),
    }),
    email: yup
      .string()
      .email('Invalid email format.')
      .max(255)
      .required('An email is required.'),
  }),
});

const AccountTeam: React.FC<IProps> = ({ account }) => {
  const [userExistError, setUserExistError] = useState<boolean>(false);

  // Form management
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
    reset: resetForm,
    clearErrors,
    watch,
  } = useForm<Account_AccountData_account_users>({
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  /**
   * Hooks
   */

  // Notification
  const notifier = useNotifier();

  // useEffect(() => {
  //   const subscription = watch((value, { name, type }) => clearErrors());
  //   return () => subscription.unsubscribe();
  // }, [watch, clearErrors]);

  /**
   * Handlers
   */

  const onCompleted = () => {
    setUserExistError(false);
    notifier.showMessage(
      'Successfully saved!',
      'Team configuration has been updated.',
      'success'
    );
  };

  // Add User Graph Mutation
  const [addAccountUser] = useMutation(ADD_ACCOUNT_USER, {
    onCompleted,
    onError: (data) => {
      if (data.graphQLErrors?.length > 0) {
        if (data.graphQLErrors[0]?.extensions?.code === 'USER_ALREADY_EXISTS') {
          setUserExistError(true);
        }
      }
    },
  });
  // Remove User Graph Mutation
  const [removeAccountUser] = useMutation(REMOVE_ACCOUNT_USER, {
    onCompleted,
  });
  // Update User Graph Mutation
  const [updateAccountUser] = useMutation(UPDATE_ACCOUNT_USER, {
    onCompleted,
  });

  const onUserRemove = (user: Account_AccountData_account_users) => {
    setUserExistError(false);
    removeAccountUser({
      variables: {
        accountId: account.id,
        userId: user.id,
      },
    });
  };

  const onRoleChange = (
    user: Account_AccountData_account_users,
    role: string
  ) => {
    setUserExistError(false);
    updateAccountUser({
      variables: {
        accountId: account.id,
        userId: user.id,
        user: { roles: [role] },
      },
    });
  };

  // On new user addition
  const onSubmit = (values: Account_AccountData_account_users): void => {
    setUserExistError(false);
    values.username = values.person!.email;
    addAccountUser({
      variables: {
        accountId: account.id,
        user: values,
      },
    });
  };

  return (
    <div className="h-full shadow rounded-lg bg-white">
      <SimpleHeader
        title="Team Management"
        subtitle="Manage the account users and permissions."
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="px-6 pb-10 flex flex-col items-center">
          <div className=" w-full space-y-2">
            <div className="space-y-1 border-b border-gray-200 pb-5">
              <InputGroup label="Add Team Members">
                <div className="flex space-x-3">
                  <div className="flex-grow">
                    <InputField
                      type="text"
                      {...register('person.name.full')}
                      error={(errors.person as any)?.name?.full}
                      placeholder="Full Name"
                      id="fullName"
                      defaultValue=""
                    />
                  </div>

                  <div className="flex-grow">
                    <InputField
                      type="text"
                      {...register('person.email')}
                      error={(errors.person as any)?.email}
                      placeholder="Email address"
                      id="email"
                      defaultValue=""
                    />
                  </div>
                  <div className="flex-grow">
                    <select
                      id="role"
                      {...register('roles.0')}
                      className="block w-full shadow-sm focus:ring-primary-light focus:border-primary-light sm:text-sm border-gray-300 rounded-md"
                      defaultValue="STAFF"
                    >
                      <option value="STAFF">Staff</option>
                      <option value="MANAGER">Manager</option>
                      <option value="ADMIN">Admin</option>
                    </select>
                  </div>
                  <span className="">
                    <Button
                      variant="secondary"
                      type="submit"
                      disabled={
                        !!(errors?.person as any)?.name?.full ||
                        !!(errors?.person as any)?.email ||
                        !getValues('person.name.full') ||
                        !getValues('person.email')
                      }
                    >
                      <PlusSmIcon className="-ml-2 mr-1 h-5 w-5 text-gray-400" />
                      <span>Add</span>
                    </Button>
                  </span>
                </div>
              </InputGroup>
              <div>
                {userExistError && (
                  <p className="mt-2 text-sm text-red-600 flex space-x-2">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                    <span>User already exists</span>
                  </p>
                )}
              </div>
            </div>
            <div className="border-b border-gray-200">
              <ul className="divide-y divide-gray-200">
                {account.users &&
                  account.users.length > 0 &&
                  account.users.map((user, idx) => (
                    <li key={idx} className="py-4 flex flex-col space-y-4">
                      <div className="flex">
                        {_.get(user, 'person.avatar.filePath', false) ? (
                          <img
                            className="h-12 w-12 rounded-full"
                            src={_.get(user, 'person.avatar.filePath', '')}
                            alt="Headshot"
                          />
                        ) : (
                          <svg
                            className="h-12 w-12 rounded-full text-gray-300"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                        )}
                        <div className="w-full flex justify-between items-center">
                          <div className="ml-3 flex flex-col">
                            <span className="text-sm font-medium text-gray-900">
                              {_.get(user, 'person.name.full', '')}
                            </span>
                            <span className="text-sm text-gray-500">
                              {_.get(user, 'person.email', '')}
                            </span>
                          </div>
                          <div className="mr-3 flex space-x-3 items-center">
                            <span
                              className={`${
                                _.get(user, 'state[0].status', null) ===
                                'ACTIVE'
                                  ? 'text-green-800 bg-green-100'
                                  : _.get(user, 'state[0].status', null) ===
                                    'PENDING'
                                  ? 'text-yellow-800 bg-yellow-100'
                                  : _.get(user, 'state[0].status', null) ===
                                    'INACTIVE'
                                  ? 'text-gray-800 bg-gray-100'
                                  : 'text-gray-800 bg-gray-100'
                              } mr-8 px-2 py-1 text-xs font-medium rounded-full`}
                            >
                              {_.get(user, 'state[0].status', 'INACTIVE')
                                .charAt(0)
                                .toUpperCase() +
                                _.get(user, 'state[0].status', 'INACTIVE')
                                  .toLowerCase()
                                  .slice(1)}
                            </span>
                            {_.get(user, `roles[0]`, null) === 'owner' ? (
                              <>
                                <button
                                  className="items-left text-sm font-medium text-gray-900 border px-6 py-2 border-gray-300 rounded-md"
                                  type="button"
                                >
                                  Owner
                                </button>
                                <p className="mr-4 px-2 py-1 text-xs font-medium rounded-full text-gray-700 bg-blue-100">
                                  Permanent
                                </p>
                              </>
                            ) : (
                              <>
                                <select
                                  name="update_team_member_role"
                                  id="update_team_member_role"
                                  className="block w-full shadow-sm focus:ring-primary-light focus:border-primary-light sm:text-sm border-gray-300 rounded-md"
                                  placeholder="update_team_member_role"
                                  aria-describedby="update_team_member_role"
                                  value={
                                    (_.get(user, 'roles', false) || [
                                      'STAFF',
                                    ])[0] || 'STAFF'
                                  }
                                  onChange={(e) => {
                                    onRoleChange(user!, e.target.value);
                                  }}
                                >
                                  <option value="STAFF">Staff</option>
                                  <option value="MANAGER">Manager</option>
                                  <option value="ADMIN">Admin</option>
                                </select>

                                <span className="text-sm font-medium text-gray-900">
                                  <button
                                    type="button"
                                    className="bg-white inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
                                    onClick={() => onUserRemove(user!)}
                                  >
                                    {/* Heroicon name: solid/user-remove */}
                                    <svg
                                      className="h-5 w-5 text-gray-400"
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6"
                                      />
                                    </svg>
                                  </button>
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AccountTeam;

const ADD_ACCOUNT_USER = gql`
  mutation AccountTeam_AddAccountUser($accountId: String!, $user: UserInput!) {
    accountUserAdd(accountId: $accountId, user: $user) {
      id
      scope
      state {
        status
        setAt
      }
      roles
      users {
        id
        username
        state {
          status
          setAt
        }
        roles
        scope
        person {
          name {
            first
            last
          }
          email
        }
      }
    }
  }
`;

const UPDATE_ACCOUNT_USER = gql`
  mutation AccountTeam_UpdateAccountUser(
    $accountId: String!
    $userId: String!
    $user: UserInput!
  ) {
    accountUserUpdate(accountId: $accountId, userId: $userId, user: $user) {
      id
      scope
      state {
        status
        setAt
      }
      roles
      users {
        id
        username
        state {
          status
          setAt
        }
        roles
        scope
        person {
          name {
            first
            last
          }
          email
        }
      }
    }
  }
`;

const REMOVE_ACCOUNT_USER = gql`
  mutation AccountTeam_RemoveAccountUser(
    $accountId: String!
    $userId: String!
  ) {
    accountUserDelete(accountId: $accountId, userId: $userId) {
      id
      scope
      state {
        status
        setAt
      }
      roles
      users {
        id
        username
        state {
          status
          setAt
        }
        roles
        scope
        person {
          name {
            first
            last
          }
          email
        }
      }
    }
  }
`;

//////
// Abstraction of components created during Profile JSX cleanup
//

//
/**
 * TODO: Create/Move to /components/InputGroup.tsx and import
 */
const InputGroup = ({
  label,
  children,
  ...props
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <label {...props} className="block text-sm font-medium text-gray-700">
      {label}
    </label>
    {children}
  </div>
);
/**
 * TODO: Create/Move to /components/InputField.tsx and import
 */
const InputField = React.forwardRef<
  HTMLInputElement,
  {
    error?: any;
    [x: string]: any;
  }
>(({ error, ...props }, ref) => (
  <div className="relative">
    <input
      ref={ref}
      {...props}
      className={`${
        error
          ? `border-red-300 placeholder-red-300 focus:ring-red-500 focus:border-red-500`
          : `border-gray-300 placeholder-gray-400 focus:ring-primary-light focus:border-primary-light`
      } appearance-none block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none sm:text-sm`}
    />
    {error ? (
      <div className="absolute inset-y-0 -top-7 right-0 pr-3 flex items-center pointer-events-none">
        <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
      </div>
    ) : null}
    <p className="mt-2 text-sm text-red-600">{error?.message}</p>
  </div>
));
/**
 * TODO: Create/Move to /components/Button.tsx and import
 */
// MB comment: Based on Mark's button as per Profile.tsx
// Enhancements: added the "disabled" attribute with disabled logic and associated UI feedbacks
// Currently, only variant=secondary is fully tested, the rest of variants are not tested yet.
const Button = ({
  variant,
  type = 'button',
  disabled = false,
  children,
  className = '',
  onClick,
}: {
  variant: string;
  type: 'button' | 'submit';
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
  onClick?: any;
}) => {
  return variant === 'primary' ? (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${
        disabled
          ? 'cursor-not-allowed hover:bg-primary'
          : 'hover:bg-primary-dark'
      }  bg-primary border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light ${className}`}
    >
      {children}
    </button>
  ) : variant === 'secondary' ? (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${
        disabled ? 'cursor-not-allowed hover:bg-white ' : 'hover:bg-gray-50'
      }  bg-white inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light ${className}`}
    >
      {children}
    </button>
  ) : variant === 'primary icon' ? (
    <button
      type={type}
      className={`${
        disabled
          ? 'cursor-not-allowed hover:bg-primary'
          : 'hover:bg-primary-dark'
      }  bg-primary border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  ) : variant === 'secondary icon' ? (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${
        disabled ? 'cursor-not-allowed hover:bg-white' : 'hover:bg-gray-50'
      }  bg-white inline-flex items-center px-2 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light ${className}`}
    >
      {children}
    </button>
  ) : null;
};
/**
 * TODO: Create/Move to /components/SimpleHeader.tsx and import
 */
const SimpleHeader = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) => (
  <div className="py-6 px-4 sm:p-6 lg:pb-8">
    <h2 className="text-lg leading-6 font-medium text-gray-900">{title}</h2>
    <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
  </div>
);
